import React, { useRef, useEffect, useState } from "react";
import Chart from "chart.js";
import s from "./CobWeb.less";
import {
  useGetCheckListIndicatorStatsLazyQuery,
  useGetDealerQuery,
} from "~/schemas/generated/graphql";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import Button from "~/shared/ui/Fields/Button";
import Back from "~/components/Icons/arrow-prev2";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import DealerInfoPanel from "../DealerInfoPanel";
import { PKDBreadcrams } from "~/widgets/PKDBreadcrams";

interface IParams {
  id: string;
}

const CobWebDepartment = () => {
  const currentDealerId = useCurrentDealerId();
  const { t } = useTranslation();
  const { id } = useParams<IParams>();
  const { data: dealerData, loading: dealerLoading } = useGetDealerQuery({
    variables: {
      DealerId: currentDealerId,
    },
    fetchPolicy: "no-cache",
  });

  const [checkListIndicatorData, { loading: indicatorLoading, error, data }] =
    useGetCheckListIndicatorStatsLazyQuery({
      fetchPolicy: "no-cache",
    });

  const [isEmpty, setIsEmpty] = useState(false);
  const idsArray = Array.isArray(id)
    ? id
    : id.split(",").map((item) => item.trim());

  useEffect(() => {
    if (dealerData?.DealerGetter?.getDealer?.autoCRMID) {
      checkListIndicatorData({
        variables: {
          dealerAutoCrmid: dealerData.DealerGetter.getDealer.autoCRMID,
          departmentIds: idsArray,
        },
      });
    }
  }, [dealerData, checkListIndicatorData]);

  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scale: {
      ticks: {
        beginAtZero: true,
        min: 0,
        max: 100,
        stepSize: 10,
      },
      gridLines: {
        color: "#e4e4e4",
      },
      angleLines: {
        color: "#e4e4e4",
      },
    },
    legend: {
      display: false,
    },
    tooltips: {
      enabled: true,
      callbacks: {
        label: (tooltipItem, data) => {
          const dataset = data.datasets[tooltipItem.datasetIndex].label || "";
          const value = tooltipItem.yLabel;
          return `${dataset}: ${value.toFixed(0)}%`;
        },
      },
    },
  };

  useEffect(() => {
    if (chartRef.current && !indicatorLoading && data) {
      const stats = data.CheckListIndicatorGetter.getCheckListIndicatorStats;
      if (stats.length === 0) {
        setIsEmpty(true);
        return;
      } else {
        setIsEmpty(false);
      }

      const scaledValues = stats.map((item) => ({
        name: item.name,
        value: item.value,
      }));

      const scaledData = {
        labels: scaledValues.map((item) => item.name),
        datasets: [
          {
            label: "",
            data: scaledValues.map((item) => item.value),
            backgroundColor: "rgba(26,149,251,0.2)",
            borderColor: "#1a95fb",
            pointRadius: 0,
            pointHoverRadius: 4,
          },
        ],
      };

      new Chart(chartRef.current, {
        type: "radar",
        data: scaledData,
        options,
      });
    }
  }, [data, indicatorLoading]);

  const history = useHistory();

  const goBack = () => {
    history.replace("/pkd/");
  };
  return (
    <>
      <PKDBreadcrams />
      <div className={s.back}>
        <Button txtBtn={t("ui.back")} onClick={goBack} small>
          <Back />
        </Button>
      </div>
      <div style={{ paddingTop: "16px" }}>
        <DealerInfoPanel type="Staff" />
      </div>
      <div className={s.wrapperChart}>
        {isEmpty ? (
          <div className={s.noData}>{t(`pkd_indicators.no_data`)}</div>
        ) : (
          <canvas ref={chartRef} width={400} height={400}></canvas>
        )}
      </div>
    </>
  );
};

export default CobWebDepartment;
